import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useConfirm } from '@/hooks/useConfirm';

interface AdminUser {
  id: string;
  email: string;
  role: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  tenantId?: string;
}

const ROLES = ['owner', 'admin', 'editor', 'viewer'] as const;

const ROLE_LABELS: Record<string, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  editor: 'Editor',
  viewer: 'Leitor',
};

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-slate-700';

const labelClass =
  'mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400';

type FormMode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; user: AdminUser };

interface TenantOption {
  id: string;
  name: string;
  isActive: boolean;
}

const emptyForm = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'editor',
  password: '',
  isActive: true,
  tenantId: '',
};

const UsersAdminPage: React.FC = () => {
  const { t } = useTranslation('common');
  const tt = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback });
  const { confirm } = useConfirm();
  const meId = useAuthStore((s) => s.user?.id);
  const myRole = useAuthStore((s) => s.user?.role);
  const myTenantId = useAuthStore((s) => s.user?.tenantId);
  const isAdmin = myRole === 'owner' || myRole === 'admin';

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<FormMode>({ kind: 'closed' });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [platform, setPlatform] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/users');
      setUsers(r.data?.users ?? []);
      const isPlatform = r.data?.isPlatformAdmin === true;
      setPlatform(isPlatform);
      if (isPlatform) {
        const tr = await api.get('/tenants');
        setTenants(tr.data?.tenants ?? []);
      }
    } catch {
      toast.error(
        tt('usersAdmin.toasts.loadError', 'Não foi possível carregar os usuários'),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const openCreate = () => {
    setForm({ ...emptyForm, tenantId: myTenantId ?? '' });
    setMode({ kind: 'create' });
  };

  const openEdit = (u: AdminUser) => {
    setForm({
      email: u.email,
      firstName: u.firstName ?? '',
      lastName: u.lastName ?? '',
      role: u.role,
      password: '',
      isActive: u.isActive,
      tenantId: u.tenantId ?? '',
    });
    setMode({ kind: 'edit', user: u });
  };

  const closeForm = () => setMode({ kind: 'closed' });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (mode.kind === 'create') {
        await api.post('/users', {
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          role: form.role,
          password: form.password,
          ...(platform && form.tenantId ? { tenantId: form.tenantId } : {}),
        });
        toast.success(tt('usersAdmin.toasts.created', 'Usuário criado'));
      } else if (mode.kind === 'edit') {
        const body: Record<string, unknown> = {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          role: form.role,
          isActive: form.isActive,
        };
        if (form.password) body.newPassword = form.password;
        if (
          platform &&
          form.tenantId &&
          form.tenantId !== mode.user.tenantId
        ) {
          body.tenantId = form.tenantId;
        }
        await api.patch(`/users/${mode.user.id}`, body);
        toast.success(tt('usersAdmin.toasts.updated', 'Usuário atualizado'));
      }
      closeForm();
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: unknown } } })
        ?.response?.data?.message;
      toast.error(
        typeof msg === 'string'
          ? msg
          : tt('usersAdmin.toasts.saveError', 'Erro ao salvar o usuário'),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u: AdminUser) => {
    if (u.id === meId) return;
    try {
      await api.patch(`/users/${u.id}`, { isActive: !u.isActive });
      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, isActive: !u.isActive } : x)),
      );
    } catch {
      toast.error(tt('usersAdmin.toasts.updateError', 'Erro ao atualizar o usuário'));
    }
  };

  const remove = async (u: AdminUser) => {
    const name = u.firstName ? `${u.firstName} ${u.lastName}`.trim() : u.email;
    const confirmed = await confirm({
      title: tt('usersAdmin.confirm.deleteTitle', 'Excluir usuário'),
      message: `Tem certeza que deseja excluir ${name}? Esta ação não pode ser desfeita.`,
      confirmText: tt('usersAdmin.actions.delete', 'Excluir'),
      type: 'danger',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(tt('usersAdmin.toasts.deleted', 'Usuário excluído'));
    } catch {
      toast.error(tt('usersAdmin.toasts.deleteError', 'Erro ao excluir o usuário'));
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? '—'
      : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
        <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-slate-400" />
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {tt(
            'usersAdmin.noPermission',
            'Você não tem permissão para gerenciar usuários.',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
            <UserRound className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {tt('usersAdmin.title', 'Usuários')}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {tt(
                'usersAdmin.subtitle',
                'Gerencie quem acessa os formulários deste tenant',
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
        >
          <Plus className="h-4 w-4" />
          {tt('usersAdmin.actions.new', 'Novo usuário')}
        </button>
      </div>

      {mode.kind !== 'closed' && (
        <form
          onSubmit={submit}
          className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {mode.kind === 'create'
                ? tt('usersAdmin.form.newTitle', 'Criar usuário')
                : tt('usersAdmin.form.editTitle', 'Editar usuário')}
            </h2>
            <button
              type="button"
              onClick={closeForm}
              className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
              aria-label={tt('usersAdmin.actions.close', 'Fechar')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="u-email">
                {tt('usersAdmin.fields.email', 'E-mail')}
              </label>
              <input
                id="u-email"
                type="email"
                required={mode.kind === 'create'}
                disabled={mode.kind === 'edit'}
                className={`${inputClass} ${mode.kind === 'edit' ? 'opacity-60' : ''}`}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="u-role">
                {tt('usersAdmin.fields.role', 'Função')}
              </label>
              <select
                id="u-role"
                className={inputClass}
                value={form.role}
                disabled={mode.kind === 'edit' && mode.user.id === meId}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            {platform && (
              <div>
                <label className={labelClass} htmlFor="u-tenant">
                  {tt('usersAdmin.fields.tenant', 'Tenant')}
                </label>
                <select
                  id="u-tenant"
                  className={inputClass}
                  value={form.tenantId}
                  disabled={mode.kind === 'edit' && mode.user.id === meId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tenantId: e.target.value }))
                  }
                >
                  {tenants.map((tn) => (
                    <option key={tn.id} value={tn.id}>
                      {tn.name} ({tn.id})
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                  {tt(
                    'usersAdmin.fields.tenantHint',
                    'Tenant à qual o usuário pertence. Apenas administradores da tenant staff podem alterar.',
                  )}
                </p>
              </div>
            )}
            <div>
              <label className={labelClass} htmlFor="u-firstName">
                {tt('usersAdmin.fields.firstName', 'Nome')}
              </label>
              <input
                id="u-firstName"
                className={inputClass}
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="u-lastName">
                {tt('usersAdmin.fields.lastName', 'Sobrenome')}
              </label>
              <input
                id="u-lastName"
                className={inputClass}
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="u-password">
                {mode.kind === 'create'
                  ? tt('usersAdmin.fields.password', 'Senha')
                  : tt('usersAdmin.fields.newPassword', 'Nova senha (opcional)')}
              </label>
              <input
                id="u-password"
                type="password"
                required={mode.kind === 'create'}
                minLength={8}
                className={inputClass}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                autoComplete="new-password"
              />
            </div>
            {mode.kind === 'edit' && (
              <div className="flex items-end gap-2 pb-1">
                <input
                  id="u-active"
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400 dark:border-slate-600"
                  checked={form.isActive}
                  disabled={mode.user.id === meId}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                <label
                  htmlFor="u-active"
                  className="text-sm text-slate-700 dark:text-slate-300"
                >
                  {tt('usersAdmin.fields.active', 'Usuário ativo')}
                </label>
              </div>
            )}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {tt('usersAdmin.actions.cancel', 'Cancelar')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {tt('usersAdmin.actions.save', 'Salvar')}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3">{tt('usersAdmin.table.user', 'Usuário')}</th>
                <th className="px-5 py-3">{tt('usersAdmin.table.role', 'Função')}</th>
                {platform && (
                  <th className="px-5 py-3">{tt('usersAdmin.table.tenant', 'Tenant')}</th>
                )}
                <th className="px-5 py-3">{tt('usersAdmin.table.status', 'Status')}</th>
                <th className="px-5 py-3">{tt('usersAdmin.table.lastLogin', 'Último acesso')}</th>
                <th className="px-5 py-3 text-right">{tt('usersAdmin.table.actions', 'Ações')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {users.map((u) => {
                const self = u.id === meId;
                const name = `${u.firstName} ${u.lastName}`.trim() || u.email;
                return (
                  <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {name}
                        {self && (
                          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            {tt('usersAdmin.you', 'você')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{u.email}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {ROLE_LABELS[u.role] ?? u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        disabled={self}
                        onClick={() => toggleActive(u)}
                        className={`rounded-full px-2.5 py-1 text-xs font-medium transition disabled:cursor-not-allowed ${
                          u.isActive
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                        }`}
                      >
                        {u.isActive
                          ? tt('usersAdmin.status.active', 'Ativo')
                          : tt('usersAdmin.status.inactive', 'Inativo')}
                      </button>
                    </td>
                    {platform && (
                      <td className="px-5 py-3">
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          {u.tenantId ?? '—'}
                        </code>
                      </td>
                    )}
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {formatDate(u.lastLoginAt)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(u)}
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                          aria-label={tt('usersAdmin.actions.edit', 'Editar')}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={self}
                          onClick={() => remove(u)}
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-red-900/30"
                          aria-label={tt('usersAdmin.actions.delete', 'Excluir')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={platform ? 6 : 5}
                    className="px-5 py-12 text-center text-sm text-slate-400"
                  >
                    {tt('usersAdmin.empty', 'Nenhum usuário encontrado.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UsersAdminPage;
